import { Typography, Box, Select, MenuItem, CircularProgress, Alert, FormControl } from "@mui/material";
import { generatePayment } from "../../../api/WalletAPI";
import { handleError } from "../../../helpers/Fetchers/fetchers";
import QRCode from 'qrcode.react';
import { GeneratePaymentPayload } from "../../../models/IWallet";
import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import useSWR from 'swr';
import { PATH_NAME } from "../../../configs/pathName";
import { generalFetcher } from "../../../helpers/Fetchers/fetchers";
import { typo } from "../../../assets/fonts/font";
import colorConfigs from "../../../configs/colorConfigs";
import { getCurrentBrand } from "../../../configs/brandConfigs";

interface PayNowPaymentoption {
  transactionId: number | null;
  transactionRef: string | null;
  amount?: number;
}

const PayNowPaymentOption = ({transactionId,transactionRef,amount: propAmount}:PayNowPaymentoption) => {
  const activeBrand = getCurrentBrand();
  const [amount, setAmount] = useState<number>(propAmount ?? 5);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [paynowCode, setPaynowCode] = useState<string | null>(null);

  const navigate = useNavigate();
  const { data: paymentStatus } = useSWR(
    transactionId ? PATH_NAME.API_GET_TRANSACTION_PAYMENT(transactionId) : null, generalFetcher, {refreshInterval:3000}
  );

  const qrCodeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof propAmount === 'number') {
      setAmount(propAmount);
    }
  }, [propAmount]);

  const handleTopUp = useCallback(async () => {
    if (!transactionRef) return;

    setLoading(true);
    setError(null);
    setPaynowCode(null);

    try {
      const paymentPayload: GeneratePaymentPayload = {
        order_ref: transactionRef,
        amount: amount,
      };

      const paymentResponse = await generatePayment(paymentPayload);
      const paynowCodeResult = paymentResponse.result;

      if (!paynowCodeResult) {
        throw new Error("Failed to generate PayNow QR code.");
      }
      setPaynowCode(paynowCodeResult);
    } catch (error) {
      handleError(error, "Payment failed:");
      setError("Payment failed. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [amount, transactionRef]);

  useEffect(() => {
    if (transactionRef) {
      handleTopUp();
    }
  }, [amount, transactionRef, handleTopUp]);

  useEffect(() => {
    if (paymentStatus?.payment_complete) {
      navigate(PATH_NAME.PAYMENT_SUCCESS, { replace: true });
    }
  }, [paymentStatus, navigate]);

  return (
    <Box>
      <Box>
        {typeof propAmount !== 'number' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <FormControl sx={{ minWidth: 120 }}>
              <Select
                value={amount}
                onChange={(e) => setAmount(Number(e.target.value))}
                size="small"
                sx={{
                  ...typo.bodyContent,
                  textAlign: 'center',
                  '& .MuiSelect-select': {
                    display: 'flex',
                    justifyContent: 'center',
                    backgroundColor: colorConfigs.secondary,
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: colorConfigs.primary,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: colorConfigs.primary,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: colorConfigs.primary,
                  },
                }}
              >
                <MenuItem sx={{ ...typo.bodyContent}} value={5}>$5</MenuItem>
                <MenuItem sx={{ ...typo.bodyContent}} value={10}>$10</MenuItem>
                <MenuItem sx={{ ...typo.bodyContent}} value={20}>$20</MenuItem>
                <MenuItem sx={{ ...typo.bodyContent}} value={50}>$50</MenuItem>
                <MenuItem sx={{ ...typo.bodyContent}} value={100}>$100</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        {loading ? (
          <CircularProgress size={24} sx={{ mt: 4 }} />
        ) : (
          paynowCode && (
            <Box sx={{ mt: 4, textAlign: "center" }}>
              <div ref={qrCodeRef}>
              <Box
                sx={{
                  paddingTop:'7px',
                  paddingX:'7px',
                  display: 'inline-block',
                  justifyContent:'center',
                }}
              >
                <QRCode 
                  value={paynowCode} 
                  size={200} 
                  fgColor={activeBrand?.colors.paymentPurple}
                  includeMargin={true}
                  style={{
                    borderStyle:'solid',
                    borderWidth:'2px',
                    borderColor:colorConfigs.tertiary,
                    borderRadius: '8px',
                  }}
                />
              </Box>  
              </div>
              <Box sx={{ paddingBottom: 10, display:'flex', flexDirection:'column',alignItems:'center' }}>
                <Box
                  sx={{
                    backgroundColor: colorConfigs.quaternary,
                    borderStyle: 'solid',
                    borderWidth: 1,
                    borderRadius: 1,
                    mt: 4,
                    mb: 1,
                    paddingX: '20px',
                    paddingY:'10px',
                    width:{xs:"100%", sm:"50%"}
                  }}
                >
                  <Typography
                    sx={{
                      ...typo.bodyContent,
                      fontSize: '14px',
                      textAlign: 'left', // Ensure text aligns to the left
                    }}
                  >
                    1. Take a screenshot of the PayNow QR code on your phone
                  </Typography>
                </Box>
                <Box
                  sx={{
                    backgroundColor: colorConfigs.quaternary,
                    borderStyle: 'solid',
                    borderWidth: 1,
                    borderRadius: 1,
                    mb: 1,
                    paddingX: '20px',
                    paddingY:'10px',
                    width:{xs:"100%", sm:"50%"}
                  }}
                >
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                      ...typo.bodyContent,
                      fontSize: '14px',
                      textAlign: 'left', // Ensure text aligns to the left
                    }}
                  >
                    2. Open your preferred banking app to scan QR code for payment
                  </Typography>
                </Box>
                <Box
                  sx={{
                    backgroundColor: colorConfigs.quaternary,
                    borderStyle: 'solid',
                    borderWidth: 1,
                    borderRadius: 1,
                    mb: 1,
                    paddingX: '20px',
                    paddingY:'10px',
                    width:{xs:"100%", sm:"50%"}

                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      ...typo.bodyContent,
                      fontSize: '14px',
                      textAlign: 'left',
                    }}
                  >
                    3. Upload the screenshot of the QR code to make payment
                  </Typography>
                </Box>
              </Box>
            </Box>
          )
        )}
      </Box>
    </Box>
  );
};

export default PayNowPaymentOption;
